import {roles, t} from '@eitje/bridge'
import utils from '@eitje/utils'

// groups items based on a property, specified by the by property
// items: array of objects to be grouped
// groupProps: object containing the following properties:
// 	by: the property to group by
// 	items: array of objects representing the groups
// 	labelField: the property to use as the label for the groups (default: by)
// 	matchField: the property to use as the match field for the groups (default: id)
// returns: array of objects representing the grouped items, with the following properties:
// 	label: the label for the group
// 	items: array of objects representing the items in the group
const groupItems = (items, {by, unknownLabel = t('unknown'), items: groupItems, labelField = by, matchField = 'id'}) => {
	if (!groupItems) {
		groupItems = _.uniqBy(items, by)
		matchField = by // if no group items are passed, use the field itself as the match field
	}

	const groupMap = _.keyBy(groupItems, matchField)

	// group items based on their relationship to groupItems
	const grouped = {}

	items.forEach(item => {
		const groupVals = utils.alwaysArray(item[by])
		groupVals.forEach(groupVal => {
			const group = groupMap[groupVal]
			const groupKey = group ? group[labelField] : unknownLabel
			grouped[groupKey] ??= []
			grouped[groupKey].push(item)
		})
	})

	const result = []

	// iterate over groupItems in its original order, and pick the corresponding items from grouped
	for (const group of groupItems) {
		const label = group[labelField]
		const items = grouped[label] || []
		if (items.length > 0) {
			result.push({label, items})
		}
	}

	// add items that are not in a group at the end
	if (grouped[unknownLabel]) {
		result.push({
			label: unknownLabel,
			items: grouped[unknownLabel],
		})
	}

	return result
}

// normalizes items array into a flat structure with type information and handles nested items
export const normalizeItems = ({items, groupProps, labelField, valueField}) => {
	let normalizedItems
	const subItems = []

	const getItemData = item => ({
		...item,
		disabled: item.disabled || (item.enableRole && roles.hooklessRoleMessage({...item, roles: item.enableRole})),
		label: item[labelField],
		value: item[valueField],
		subItems: item.subItems && item.subItems.map(getItemData),
	})

	// normalizes a single item into the desired format, while taking care of grouped and nested items
	const processItem = (item, group) => {
		const hasSubItems = item.subItems?.length > 0
		if (hasSubItems) subItems.push(...item.subItems.map(subItem => ({type: 'nested-item', data: getItemData(subItem)})))
		const base = {type: hasSubItems ? 'nested-group' : 'item', data: getItemData(item)}
		return group ? {...base, group} : base
	}

	if (groupProps) {
		const grouped = groupItems(items, groupProps)
		const result = grouped.flatMap(({label, items}) => {
			const group = {type: 'group', label, items: items.map(getItemData)}
			const processedItems = items.map(item => processItem(item, label))
			return [group, ...processedItems]
		})

		normalizedItems = result.map((item, index) => ({
			...item,
			originalIndex: index,
		}))
	} else {
		normalizedItems = items.map((item, index) => ({
			...processItem(item),
			originalIndex: index,
		}))
	}

	return [...normalizedItems, ...subItems]
}
